import React, { useState } from 'react';
import { Camera, X } from 'lucide-react';
import { motion } from 'framer-motion';
import AlertDialog from './AlertDialog';
import { useAlertDialog } from '../hooks/useAlertDialog';

interface PhotoUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, caption: string, uploader: string) => Promise<void>;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({ isOpen, onClose, onUpload }) => {
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [uploader, setUploader] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { isOpen: alertIsOpen, title, message, setAlert, clearAlert } = useAlertDialog();
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Create preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreview(fileReader.result as string);
      };
      fileReader.readAsDataURL(selectedFile);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !uploader) return;
    
    setIsUploading(true);
    try {
      await onUpload(file, caption, uploader);
      // Reset form
      setFile(null);
      setCaption('');
      setUploader('');
      setPreview(null);
      onClose();
    } catch (error) {
      console.error('Error uploading photo:', error);
      setAlert('Erro', 'Erro ao enviar foto. Por favor, tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div 
          className="bg-white rounded-lg shadow-xl max-w-lg w-full"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-xl font-playfair font-medium text-wedding-blue-800">
              Compartilhar Foto
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-center">
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-full max-h-64 object-contain rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setPreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Camera size={36} className="text-gray-400 mb-2" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Clique para selecionar</span> ou arraste uma foto
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG ou JPEG (máx. 10MB)</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </label>
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="uploader" className="block text-sm font-medium text-gray-700 mb-1">
                Seu nome
              </label>
              <input
                id="uploader"
                type="text"
                value={uploader}
                onChange={(e) => setUploader(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="caption" className="block text-sm font-medium text-gray-700 mb-1">
                Legenda (opcional)
              </label>
              <input
                id="caption"
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
              />
            </div>
            
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isUploading || !file || !uploader}
                className="px-4 py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isUploading ? 'Enviando...' : 'Compartilhar Foto'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      <AlertDialog
        isOpen={alertIsOpen}
        title={title}
        message={message}
        onClose={clearAlert}
      />
    </>
  );
};

export default PhotoUpload;